// service-routing.ts
/**
 * This file creates the interface definitions for the Jarvis Service Routing Plugin.
 * The Service Routing Plugin exposes to web applications the capabilities of the
 * Jarvis Service Routing Framework, which allows clients to launch known UI workflows
 * (called "services" in this context) in native apps.
 * To be "known", a service must be registered by the native app with the
 * Client Service Routing Component or must be included in the app's service manifest
 * which is retrieved from the Service Routing Cloud Service by the Cloud Service
 * Routing Component.
 *
 * This specification only intends to define the Typescript interface that a web app uses,
 * and does not define any native/platform-specific APIs.
 * @packageDocumentation
 * @module ServiceRoutingPlugin
 */
/**
 * Indicates the state of a service instance
 */
export var ServiceInstanceState;
(function (ServiceInstanceState) {
    /**
     * Indicates that the service instance is in the process of launching.  This is the initial
     * state for a service instance
     */
    ServiceInstanceState["launching"] = "launching";
    /**
      * Indicates that the service instance successfully launched and is still running
      */
    ServiceInstanceState["running"] = "running";
    /**
     * Indicates that the service instance has ended and its container has closed.  This
     * is a terminal state.
     */
    ServiceInstanceState["closed"] = "closed";
    /**
     * Indicates that the service instance failed to launch.  For example, services
     * that launch in a Jarvis WebView may fail to launch when the device is not connected
     * to a network.  This is a terminal state.
     */
    ServiceInstanceState["failedToLaunch"] = "failedToLaunch";
})(ServiceInstanceState || (ServiceInstanceState = {}));
/**
 * Indicates the type of error that occurred when any of the plugin methods are called
 */
export var ServiceRoutingErrorType;
(function (ServiceRoutingErrorType) {
    /**
     * Returned when a plugin method that requires a service identifier is called with an identifier
     * that does not correspond to any available service
     */
    ServiceRoutingErrorType["serviceNotFound"] = "serviceNotFound";
    /**
     * Indicates that one or more options passed in a plugin method are invalid. The reason in
     * the service routing error should include more details about what was invalid.
     */
    ServiceRoutingErrorType["invalidOptions"] = "invalidOptions";
    /**
     * Returned when a plugin methods that requires a service instance identifier is called
     * with an identifier that does not correspond to any known service instance. Note that
     * a  service instance with this identifier may have existed at some point, but is no
     * longer recorded
     */
    ServiceRoutingErrorType["serviceInstanceNotFound"] = "serviceInstanceNotFound";
    /**
     * Returned when an unexpected error occurs in any plugin method. The reason in the service routing
     * error should include more details about the error
     */
    ServiceRoutingErrorType["unknownError"] = "unknownError";
    /**
     * Returned when a service that is requested to be launched is not supported.
     * For example, this could mean that Services or Plugins that the requested Service requires are not available.
     */
    ServiceRoutingErrorType["serviceNotSupported"] = "serviceNotSupported";
})(ServiceRoutingErrorType || (ServiceRoutingErrorType = {}));
/**
 * Indicates the behavior that should happen when the container for the service
 * closes.  Even though only one container close behavior is currently supported, this
 * is an enum because we expect to support other close behaviors in the future.
 */
export var ContainerCloseBehavior;
(function (ContainerCloseBehavior) {
    /**
     * Indicates that a transition will be made back to the screen that was showing
     * when the service was launched
     */
    ContainerCloseBehavior["returnToCaller"] = "returnToCaller";
})(ContainerCloseBehavior || (ContainerCloseBehavior = {}));
/**
 * Indicates to a launching service which animation should be displayed during the launching of
 * the service. This helps provide a logical 'flow' to a user, during the launching of services.
 */
export var TransitionType;
(function (TransitionType) {
    /**
     * Indicates that the service should launch with a 'forward' animation
     */
    TransitionType["forward"] = "forward";
    /**
     * Indicates that the service should launch with a 'backward' animation
     */
    TransitionType["backward"] = "backward";
})(TransitionType || (TransitionType = {}));
/**
 * @deprecated
 * Indicates the size, location, and modality of the the service container
 */
export var ContainerViewType;
(function (ContainerViewType) {
    /**
     * @deprecated
     * Indicates the container will fill the entire screen.  "entire screen" may include the
     * platform's usual navbar space, depending on the value of the
     * `FullScreenContainerViewOptions.belowNavBar` option used for this container view type.
     */
    ContainerViewType["fullScreen"] = "fullScreen";
    /**
     * Indicates the container will be shown in a platform-specific modal view
     */
    ContainerViewType["modal"] = "modal";
    /**
     * Indicates that the service will choose how the container will be presented.  This would be
     * advertised as supported by a service in a couple of cases:
     *  - The service does not provide any flexibility in how the container will be presented.
     *    In this case, this would be the only container view type advertised as supported by
     *    the service.  For example: a service that launches an external app, would typically not
     *    allow a client to determine how the container gets presented
     *  - The service has a default container view type, but allows clients to specify
     *    a specific container view type as an alternative if desired.
     */
    ContainerViewType["determinedByService"] = "determinedByService";
})(ContainerViewType || (ContainerViewType = {}));
/**
 * The types of events that are emitted by the Eventing Service, on behalf of the Service Routing Component.
 */
export var ServiceInstanceEvent;
(function (ServiceInstanceEvent) {
    /**
     * The event emitted when the service instance state has changed.
     */
    ServiceInstanceEvent["jarvisEventServiceInstanceStateChanged"] = "com.hp.jarvis.servicerouting.serviceinstance.statechanged";
    /**
     * The event emitted when a service instance has closed.
     * *Optional* Service result data will be contained inside the eventData, in the 'extraData' field.
     */
    ServiceInstanceEvent["jarvisEventServiceInstanceClosed"] = "com.hp.jarvis.servicerouting.serviceinstance.closed";
    /**
     * The event emitted when an error in a service instance occurs.
     */
    ServiceInstanceEvent["jarvisEventServiceInstanceError"] = "com.hp.jarvis.servicerouting.serviceinstance.error";
})(ServiceInstanceEvent || (ServiceInstanceEvent = {}));
/**
 * Defines service identifiers reserved by the system for built-in services
 */
export var ReservedServiceIdentifier;
(function (ReservedServiceIdentifier) {
    /**
     * The identifier of the built-in Open URL service
     */
    ReservedServiceIdentifier["openUrl"] = "openUrl";
})(ReservedServiceIdentifier || (ReservedServiceIdentifier = {}));
//# sourceMappingURL=service-routing.js.map